import type { Color, FilterLayer, FilterOf, FilterType, GradientStop } from "./document";
import { newId } from "./factory";

/**
 * Metadata for the image filter stack (document.ts `FilterLayer`): menu groups, labels, and
 * each parameter's control, range and default. The ranges here must stay inside the schema's
 * bounds (filters.test.ts checks every one), so the UI can never produce an invalid document.
 */
export const FILTER_GROUPS = ["Blur", "Sharpen", "Noise", "Stylise", "Colour"] as const;
export type FilterGroup = (typeof FILTER_GROUPS)[number];

interface Base<K> {
  key: K;
  label: string;
}
/** `scale` maps the stored value to the displayed one (0..1 → %). */
export type NumberParam<K = string> = Base<K> & { kind: "number"; min: number; max: number; step: number; default: number; suffix?: string; scale?: number; int?: boolean };
export type ParamSpec<K = string> =
  | NumberParam<K>
  | (Base<K> & { kind: "bool"; default: boolean })
  | (Base<K> & { kind: "enum"; options: readonly string[]; default: string })
  | (Base<K> & { kind: "color"; default: Color })
  | (Base<K> & { kind: "stops"; default: GradientStop[] });

export interface FilterSpec<T extends FilterType = FilterType> {
  label: string;
  group: FilterGroup;
  params: ParamSpec<keyof FilterOf<T>["params"] & string>[];
}

const num = <K extends string>(key: K, label: string, min: number, max: number, def: number, extra: Partial<Omit<NumberParam, "key">> = {}): NumberParam<K> => ({
  label, kind: "number", min, max, step: 1, default: def, ...extra, key,
});
const px = { suffix: "px" };
const deg = { suffix: "°" };
const pct = { suffix: "%" };
const unit = { suffix: "%", scale: 100, step: 0.01 };
const int = { int: true };

export const FILTER_SPECS: { [T in FilterType]: FilterSpec<T> } = {
  gaussianBlur: { label: "Gaussian blur", group: "Blur", params: [num("radius", "Radius", 0, 250, 4, { ...px, step: 0.1 })] },
  motionBlur: { label: "Motion blur", group: "Blur", params: [num("angle", "Angle", -180, 180, 0, deg), num("distance", "Distance", 0, 1000, 30, px)] },
  radialBlur: {
    label: "Radial blur",
    group: "Blur",
    params: [
      { key: "mode", label: "Method", kind: "enum", options: ["spin", "zoom"], default: "zoom" },
      num("amount", "Amount", 0, 100, 20),
      num("cx", "Centre X", 0, 1, 0.5, unit),
      num("cy", "Centre Y", 0, 1, 0.5, unit),
    ],
  },
  lensBlur: {
    label: "Lens blur",
    group: "Blur",
    params: [num("radius", "Radius", 0, 100, 12, { ...px, step: 0.1 }), num("brightness", "Bokeh brightness", 0, 100, 40), num("threshold", "Bokeh threshold", 0, 255, 200, int)],
  },
  tiltShift: {
    label: "Tilt-shift",
    group: "Blur",
    params: [num("blur", "Blur", 0, 100, 15, px), num("y", "Focus position", 0, 1, 0.5, unit), num("angle", "Angle", -90, 90, 0, deg), num("focus", "Focus size", 0, 1, 0.12, unit), num("transition", "Transition", 0, 1, 0.25, unit)],
  },
  unsharpMask: {
    label: "Unsharp mask",
    group: "Sharpen",
    params: [num("amount", "Amount", 0, 500, 100, pct), num("radius", "Radius", 0.1, 250, 1.5, { ...px, step: 0.1 }), num("threshold", "Threshold", 0, 255, 0, int)],
  },
  highPass: { label: "High pass", group: "Sharpen", params: [num("radius", "Radius", 0.1, 250, 8, { ...px, step: 0.1 })] },
  addNoise: { label: "Add noise", group: "Noise", params: [num("amount", "Amount", 0, 100, 12, pct), { key: "mono", label: "Monochromatic", kind: "bool", default: false }] },
  reduceNoise: { label: "Reduce noise", group: "Noise", params: [num("strength", "Strength", 0, 10, 6, { step: 0.1 }), num("preserveDetails", "Preserve details", 0, 100, 60, pct)] },
  emboss: { label: "Emboss", group: "Stylise", params: [num("angle", "Angle", -180, 180, 135, deg), num("height", "Height", 1, 100, 3, px), num("amount", "Amount", 1, 500, 100, pct)] },
  findEdges: { label: "Find edges", group: "Stylise", params: [] },
  pixelate: { label: "Pixelate", group: "Stylise", params: [num("cell", "Cell size", 2, 200, 12, px)] },
  halftone: { label: "Colour halftone", group: "Stylise", params: [num("size", "Dot size", 2, 100, 8, px), num("angle", "Angle", -180, 180, 45, deg)] },
  oilPaint: { label: "Oil paint", group: "Stylise", params: [num("radius", "Brush size", 1, 50, 4, { ...px, step: 0.5 })] },
  invert: { label: "Invert", group: "Colour", params: [] },
  posterize: { label: "Posterise", group: "Colour", params: [num("levels", "Levels", 2, 255, 4, int)] },
  threshold: { label: "Threshold", group: "Colour", params: [num("level", "Level", 1, 255, 128, int)] },
  gradientMap: {
    label: "Gradient map",
    group: "Colour",
    params: [{ key: "stops", label: "Gradient", kind: "stops", default: [{ offset: 0, color: "#1b1340ff" }, { offset: 0.5, color: "#d6366fff" }, { offset: 1, color: "#ffe08aff" }] }],
  },
  blackWhite: {
    label: "Black & white",
    group: "Colour",
    params: [
      num("reds", "Reds", -200, 300, 40, pct),
      num("yellows", "Yellows", -200, 300, 60, pct),
      num("greens", "Greens", -200, 300, 40, pct),
      num("cyans", "Cyans", -200, 300, 60, pct),
      num("blues", "Blues", -200, 300, 20, pct),
      num("magentas", "Magentas", -200, 300, 80, pct),
    ],
  },
  photoFilter: {
    label: "Photo filter",
    group: "Colour",
    params: [{ key: "color", label: "Colour", kind: "color", default: "#ec8a00ff" }, num("density", "Density", 0, 100, 25, pct), { key: "preserveLuminosity", label: "Preserve luminosity", kind: "bool", default: true }],
  },
};

export const FILTER_TYPES = Object.keys(FILTER_SPECS) as FilterType[];

/** A new, enabled filter of `type` with default parameters. */
export function defaultFilter<T extends FilterType>(type: T, id = newId()): FilterOf<T> {
  const params = Object.fromEntries(FILTER_SPECS[type].params.map((p) => [p.key, structuredClone(p.default)]));
  return { id, type, enabled: true, params } as unknown as FilterOf<T>;
}

/** Clamp (and round, for integer params) a value typed into a numeric filter control. */
export function clampParam(spec: NumberParam, v: number): number {
  if (!Number.isFinite(v)) return spec.default;
  const c = Math.min(spec.max, Math.max(spec.min, v));
  return spec.int ? Math.round(c) : c;
}

/** True if the stack changes the picture (any enabled layer). */
export const hasActiveFilters = (filters: FilterLayer[] | undefined): boolean => !!filters?.some((f) => f.enabled);
